// CORE
import { executeRequest, type ApiResult } from '@/core/either';
import { http } from '@/core/api';

// TYPES
import type { Transaction, TransactionPayload, PreTransactionResponse } from '@/types/TransactionType';
import type { IThirdPartyDetails } from '@/types/InterfaceThirdPartyDetails';
import type { IP2pDetail } from '@/types/InterfaceP2pDetail';
import type {
  MovementsVoucherResponse,
  InvoiceVoucher,
} from '@/types/InvoiceVoucher';
import type { ValidateRoutingNumberResponse } from '@/shared/common/types/bankingRegulator.type';
import type { IExchangeDetail } from '@/types/Exchange';
import type { AuthHeader } from '@/types/ApiHeader';
import type { QuoteRequest, QuoteResponse } from '@/types/QuoteType';

// MODULE TYPES
import type { IRemittanceDetailResponse } from '@/shared/common/types/remittance.type';

// LOCAL TYPES
import type {
  GetTransactionParams,
  GetTransactionWithSlaParams,
  GetThirdPartyParams,
  DownloadVoucherRequest,
  GetP2pDetailParams,
  GetCurrencyCloudBankListParams,
} from '../types/transaction.type';
import { DOCUMENT_VOUCHER_PDF_VERSION } from '../constants/document.constant';

export const transactionService = {
  /**
   * Obtiene el detalle de una transacción específica.
   * @param params - Parámetros que incluyen el ID de transacción y tipo de producto opcional.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<Transaction>).
   */
  getTransaction(
    params: GetTransactionParams
  ): Promise<ApiResult<Transaction>> {
    return executeRequest(() =>
      http.get<Transaction>('product-gateway/b2b/transaction', {
        params: {
          transactionId: params.transactionId,
          productType: params.productType,
        },
      })
    );
  },

  /**
   * Obtiene una transacción específica con opción de cálculo de SLA.
   * @param params - Parámetros que incluyen el ID de transacción y opción de calcular SLA.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<Transaction>).
   */
  getTransactionWithSla(
    params: GetTransactionWithSlaParams
  ): Promise<ApiResult<Transaction>> {
    return executeRequest(() =>
      http.get<Transaction>(`transaction/customer/${params.transactionId}`, {
        params: { calculateSla: params.calculateSla || false },
      })
    );
  },

  /**
   * Obtiene el detalle de una remesa por su ID de transacción.
   * @param transactionId - Identificador de la transacción/remesa.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<IRemittanceDetailResponse>).
   */
  getRemittance(
    transactionId: string | number
  ): Promise<ApiResult<IRemittanceDetailResponse>> {
    return executeRequest(() =>
      http.get<IRemittanceDetailResponse>(
        'product-gateway/b2b/transaction/remittance',
        {
          params: { transactionId },
        }
      )
    );
  },

  /**
   * Obtiene los detalles de una transacción P2P.
   * @param params - Parámetros con el ID de la transacción.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<IP2pDetail>).
   */
  getP2pRemittance(
    id: string | number
  ): Promise<ApiResult<IRemittanceDetailResponse>> {
    return executeRequest(() =>
      http.get<IRemittanceDetailResponse>(
        `product-gateway/b2b/p2p-remittances/${id}`,
        { params: { product: 'TRX_SENT' } }
      )
    );
  },

  getP2pDetail(params: GetP2pDetailParams): Promise<ApiResult<IP2pDetail>> {
    return executeRequest(() =>
      http.get<IP2pDetail>('product-gateway/b2b/p2p', { params })
    );
  },

  /**
   * Obtiene los detalles de un tercero asociado a una transacción.
   * @param params - Parámetros que incluyen el ID de transacción y ID de compañía.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<IThirdPartyDetails>).
   */
  getThirdParty(
    params: GetThirdPartyParams
  ): Promise<ApiResult<IThirdPartyDetails>> {
    return executeRequest(() =>
      http.get<IThirdPartyDetails>(`product-gateway/transaction`, {
        params: {
          transactionId: params.transactionId,
          productType: 'DEPOSIT',
          customerId: params.companyId,
        },
      })
    );
  },

  /**
   * Obtiene la lista de bancos disponibles para CurrencyCloud por producto específico.
   * @param params - Parámetros con el ID del producto.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<ValidateRoutingNumberResponse>).
   */
  getCurrencyCloudBankList(
    params: GetCurrencyCloudBankListParams
  ): Promise<ApiResult<ValidateRoutingNumberResponse>> {
    return executeRequest(() =>
      http.get<ValidateRoutingNumberResponse>('payment/b2b/accounts/metadata', {
        params,
      })
    );
  },

  /**
   * Obtiene los detalles de una conversión para una transacción específica.
   *
   * El parámetro `id` se modela como `string | number` para que la
   * capa de Credential API pueda enviar el `transactionId` canónico
   * (string) sin coerción, alineado con el resto de los servicios de
   * detalle que ya aceptan `string | number`.
   * Source: SDD change `credential-api-movement-detail-request`.
   *
   * @param id - Identificador de la transacción (numérico o string).
   * @returns Promesa con el resultado tipado usando Either (ApiResult<IExchangeDetail>).
   */
  getExchange(id: string | number): Promise<ApiResult<IExchangeDetail>> {
    return executeRequest(() =>
      http.get<IExchangeDetail>(`wallet-b2b/b2b/exchanges/${id}`)
    );
  },

  /**
   * Cancela una transacción específica.
   * @param id - Identificador numérico de la transacción a cancelar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<Transaction>).
   */
  cancelTransaction(id: number): Promise<ApiResult<Transaction>> {
    return executeRequest(() =>
      http.delete<Transaction>('transaction/customer', { params: { id } })
    );
  },

  /**
   * Descarga el comprobante de movimiento en formato PDF.
   * @param data - Objeto con la plantilla y los valores del comprobante.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<MovementsVoucherResponse>).
   */
  downloadVoucher(
    data: DownloadVoucherRequest
  ): Promise<ApiResult<MovementsVoucherResponse>> {
    return executeRequest(() =>
      http.post<MovementsVoucherResponse>(
        'document/b2b/vouchers/voucher-pdf',
        {
          ...data,
          version: DOCUMENT_VOUCHER_PDF_VERSION,
        }
      )
    );
  },

  /**
   * Descarga el certificado SWIFT de una transacción.
   * @param transactionId - ID de la transacción para descargar el certificado SWIFT.
   * @returns Promesa con el resultado tipado usando Either (Promise<ApiResult<MovementsVoucherResponse>>).
   */
  downloadSwiftCertificate(
    transactionId: string | number
  ): Promise<ApiResult<MovementsVoucherResponse>> {
    return executeRequest(() =>
      http.get<MovementsVoucherResponse>(
        `transaction/b2b/swift-voucher/${transactionId}`
      )
    );
  },

  /**
   * Descarga el comprobante del cobro USD10 por recepción WIRE/SWIFT (COBRO_PARTNER_IN).
   * @param params.partnerId - Identificador del cobro (va en el path).
   * @param params.parentId - Identificador del depósito que originó el cobro (va en el query).
   * @returns Promesa con el resultado tipado usando Either (ApiResult<MovementsVoucherResponse>).
   */
  downloadPayinFeeVoucher(params: {
    partnerId: number | string;
    parentId: number | string;
  }): Promise<ApiResult<MovementsVoucherResponse>> {
    return executeRequest(() =>
      http.get<MovementsVoucherResponse>(
        `product-gateway/b2b/payin-fee-vouchers/${params.partnerId}`,
        { params: { parentId: params.parentId } }
      )
    );
  },

  /**
   * Descarga la factura en formato PDF de una transacción.
   * @param data - Datos de la factura/voucher a generar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<BlobPart>).
   */
  downloadInvoice(data: InvoiceVoucher): Promise<ApiResult<BlobPart>> {
    return executeRequest(() =>
      http.post<BlobPart>('transaction/b2b/voucher-pdf', data, {
        responseType: 'blob',
      })
    );
  },

  /**
   * Crea una pre-transacción (transacción pendiente de aprobación).
   * @param header - Headers de autenticación (authType, authValue, businessFlow).
   * @param payload - Datos de la transacción a crear.
   * @returns Promesa con el resultado tipado usando Either (ApiResult).
   */
  createPreTransaction(
    header: AuthHeader,
    payload: TransactionPayload
  ): Promise<ApiResult<PreTransactionResponse>> {
    return executeRequest(() =>
      http.post<PreTransactionResponse>('wallet-b2b/b2b/pre-transactions', payload, {
        headers: {
          authType: header.authType,
          authValue: header.authValue,
          businessFlow: header.businessFlow,
        },
      })
    );
  },

  /**
   * Crea una cotización para una transacción.
   * @param quoteRequest - Datos de la cotización a generar.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<QuoteResponse>).
   */
  createQuote(quoteRequest: QuoteRequest): Promise<ApiResult<QuoteResponse>> {
    return executeRequest(() =>
      http.post<QuoteResponse>('quote/b2b', quoteRequest)
    );
  },
};
